import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultProcessAreaResource,
    ProcessAreaMutationResourceSchema,
    ProcessAreaPropertyValidation,
    useProcessAreaActions,
} from '../../../../../resource/resources/artifacts/process_area/processAreaResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { ProcessAreaContent } from './ProcessAreaContent';

const ProcessAreaOverviewContent = ({ id, displayedInOverview }: { id: string; displayedInOverview: boolean }) => {
    const readOnly = useSelector(selectReadOnly);

    const processAreaActions = useProcessAreaActions();
    const processAreaQuery = processAreaActions.useGetResource(id);
    const updateProcessArea = processAreaActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProcessAreaMutationResourceSchema,
        defaultValues: defaultProcessAreaResource,
        submitHandler: async (data) => {
            await updateProcessArea.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: processAreaQuery.data,
    });

    return (
        <QueryBaseComponent name="ProcessArea" query={processAreaQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <ProcessAreaContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const ProcessAreaOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const processAreaActions = useProcessAreaActions();

    const parametersQuery = processAreaActions.useGetAllResources(parentId);

    const resourceType = ResourceType.PROCESS_AREA;

    return (
        <QueryBaseComponent name="ProcessAreas" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={'Process Areas'}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <ProcessAreaOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: ProcessAreaPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
